import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import type { IUser } from "../../../model/model";
import type { IProjectGroupWithProjects } from "../../projectGroups/state/projectGroupsSlice";

export type MfaMethod = "totp" | "email";

export interface IMfaStatus {
  enabled: boolean;
  method: MfaMethod | null;
  mandatory: boolean;
  recoveryCodesRemaining: number | null;
}

export interface IMfaEnrollmentMaterial {
  mfaToken: string;
  method: MfaMethod;
  secret?: string;
  otpauthUri?: string;
  qrDataUri?: string;
}

interface IMfaState {
  status: IMfaStatus | null;
  enrollment: IMfaEnrollmentMaterial | null;
  recoveryCodes: string[] | null;
}

const initialState: IMfaState = {
  status: null,
  enrollment: null,
  recoveryCodes: null,
};

interface IMfaStatusResponse {
  enabled: boolean;
  method: MfaMethod | null;
  mandatory: boolean;
  recovery_codes_remaining: number | null;
}

interface IMfaEnrollStartResponse {
  mfa_token: string;
  method: MfaMethod;
  secret?: string;
  otpauth_uri?: string;
  qr_data_uri?: string;
}

interface IMfaStepUpSendResponse {
  ok: boolean;
  step_up_token: string;
}

export interface IMfaEnrollConfirmResponse {
  ok?: boolean;
  recovery_codes: string[];
  jwt?: string | null;
  helpDocsJwt?: string;
  user?: IUser;
  firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
}

interface IMfaRecoveryCodesResponse {
  recovery_codes: string[];
}

export const fetchMfaStatus = createAsyncThunk<IMfaStatusResponse, IAppLoaderAction>(
  "mfa/fetchStatus",
  // eslint-disable-next-line no-empty-pattern
  async ({}, { rejectWithValue }) => {
    try {
      const res = await axios.get(apiUrls.mfa.status);
      return res.data as IMfaStatusResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const startMfaEnrollment = createAsyncThunk<
  IMfaEnrollStartResponse,
  {
    method: MfaMethod;
    password?: string;
    mfaToken?: string;
    currentOtp?: string;
    recoveryCode?: string;
    stepUpToken?: string;
  } & IAppLoaderAction
>(
  "mfa/startEnrollment",
  async (
    { method, password, mfaToken, currentOtp, recoveryCode, stepUpToken },
    { rejectWithValue },
  ) => {
    try {
      const res = await axios.post(apiUrls.mfa.enrollStart, {
        method,
        password,
        mfa_token: mfaToken,
        current_otp: currentOtp,
        recovery_code: recoveryCode,
        step_up_token: stepUpToken,
      });
      return res.data as IMfaEnrollStartResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const confirmMfaEnrollment = createAsyncThunk<
  IMfaEnrollConfirmResponse,
  { mfaToken: string; otp: string } & IAppLoaderAction
>("mfa/confirmEnrollment", async ({ mfaToken, otp }, { rejectWithValue }) => {
  try {
    const res = await axios.post(apiUrls.mfa.enrollConfirm, { mfa_token: mfaToken, otp });
    return res.data as IMfaEnrollConfirmResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(err?.response?.data);
    }
    throw err;
  }
});

interface ICredentialConfirmArgs {
  password: string;
  otp?: string;
  recoveryCode?: string;
  stepUpToken?: string;
}

export const disableMfa = createAsyncThunk<unknown, ICredentialConfirmArgs & IAppLoaderAction>(
  "mfa/disable",
  async ({ password, otp, recoveryCode, stepUpToken }, { rejectWithValue }) => {
    try {
      const res = await axios.post(apiUrls.mfa.disable, {
        password,
        otp,
        recovery_code: recoveryCode,
        step_up_token: stepUpToken,
      });
      return res.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const regenerateRecoveryCodes = createAsyncThunk<
  IMfaRecoveryCodesResponse,
  ICredentialConfirmArgs & IAppLoaderAction
>(
  "mfa/regenerateRecoveryCodes",
  async ({ password, otp, recoveryCode, stepUpToken }, { rejectWithValue }) => {
    try {
      const res = await axios.post(apiUrls.mfa.regenerateRecoveryCodes, {
        password,
        otp,
        recovery_code: recoveryCode,
        step_up_token: stepUpToken,
      });
      return res.data as IMfaRecoveryCodesResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const sendMfaStepUpCode = createAsyncThunk<IMfaStepUpSendResponse, IAppLoaderAction>(
  "mfa/sendStepUpCode",
  // eslint-disable-next-line no-empty-pattern
  async ({}, { rejectWithValue }) => {
    try {
      const res = await axios.post(apiUrls.mfa.stepUpSend, {});
      return res.data as IMfaStepUpSendResponse;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue({
          ...(err.response?.data as Record<string, unknown> | undefined),
          status: err.response?.status ?? null,
        });
      }
      throw err;
    }
  },
);

export const mfaSlice = createSlice({
  name: "mfa",
  initialState,
  reducers: {
    clearEnrollmentWizardState: (state) => {
      state.enrollment = null;
      state.recoveryCodes = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMfaStatus.fulfilled, (state, { payload }) => {
      state.status = {
        enabled: payload.enabled,
        method: payload.method,
        mandatory: payload.mandatory,
        recoveryCodesRemaining: payload.recovery_codes_remaining ?? null,
      };
    });

    builder.addCase(startMfaEnrollment.fulfilled, (state, { payload }) => {
      state.enrollment = {
        mfaToken: payload.mfa_token,
        method: payload.method,
        secret: payload.secret,
        otpauthUri: payload.otpauth_uri,
        qrDataUri: payload.qr_data_uri,
      };
    });

    builder.addCase(confirmMfaEnrollment.fulfilled, (state, { payload }) => {
      if (state.status) {
        state.status.recoveryCodesRemaining = payload.recovery_codes.length;
      }
      state.recoveryCodes = payload.recovery_codes;
      state.enrollment = null;
    });

    builder.addCase(disableMfa.fulfilled, (state) => {
      state.status = {
        enabled: false,
        method: null,
        mandatory: state.status?.mandatory ?? false,
        recoveryCodesRemaining: null,
      };
    });

    builder.addCase(regenerateRecoveryCodes.fulfilled, (state, { payload }) => {
      state.recoveryCodes = payload.recovery_codes;
      if (state.status) {
        state.status.recoveryCodesRemaining = payload.recovery_codes.length;
      }
    });
  },
});

export const mfaReducer = mfaSlice.reducer;
export const { clearEnrollmentWizardState } = mfaSlice.actions;

export const getMfaStatus = (state: RootState) => state.mfa.status;
export const getMfaEnrollment = (state: RootState) => state.mfa.enrollment;
export const getMfaRecoveryCodes = (state: RootState) => state.mfa.recoveryCodes;
